import type { AugmentedFeedback } from "@productlane/api/src/lib/timeline-events"
import type {
	ThreadOlderMessagesRow,
	ThreadTimelineCursor,
} from "@productlane/zero/queries"
import {
	feedbackOlderMessagesPageSize,
	threadDetailRelationLimits,
} from "@productlane/zero/queries"

export interface TimelinePageData {
	cursor: ThreadTimelineCursor
	row: ThreadOlderMessagesRow | undefined
	complete: boolean
}

interface TimelineRow {
	readonly createdAt: number
	readonly id: string
}

// Matches Zero's sort comparator for (createdAt desc, id desc) windows.
const cmpRow = (a: TimelineRow, b: TimelineRow): number =>
	a.createdAt - b.createdAt || a.id.localeCompare(b.id)

const oldestOf = <T extends TimelineRow>(rows: readonly T[]): T | undefined => {
	let oldest: T | undefined
	for (const row of rows) {
		if (!oldest || cmpRow(row, oldest) < 0) oldest = row
	}
	return oldest
}

// Page rows come from the same tables as the base relations, so the cast to
// T is sound; it only bridges the two structurally identical row types.
const mergeRows = <T extends TimelineRow>(
	base: readonly T[] | undefined,
	pageRows: (readonly TimelineRow[] | undefined)[],
): T[] => {
	const byId = new Map<string, T>()
	for (const row of base ?? []) byId.set(row.id, row)
	for (const rows of pageRows) {
		for (const row of rows ?? []) {
			if (!byId.has(row.id)) byId.set(row.id, row as T)
		}
	}
	return [...byId.values()]
}

interface RelationGate {
	// Oldest row we can trust continuity down to; undefined when fully loaded.
	floor: TimelineRow | undefined
	// Base window slid above page 1's cursor, rows in between are unsynced.
	hole: boolean
}

// Base + pages are contiguous per relation (cursors come from the display
// boundary) unless the base window slid above cursor_1: the hole case.
const gateRelation = (
	base: readonly TimelineRow[] | undefined,
	baseDisplayLimit: number,
	pageRelationRows: (readonly TimelineRow[] | undefined)[],
	pages: TimelinePageData[],
): RelationGate => {
	const baseRows = base ?? []
	const baseTruncated = baseRows.length > baseDisplayLimit
	if (!baseTruncated) return { floor: undefined, hole: false }

	const baseFloor = oldestOf(baseRows)
	if (pages.length === 0) return { floor: baseFloor, hole: false }

	const firstCursor = pages[0]!.cursor
	if (baseFloor && cmpRow(baseFloor, firstCursor) > 0) {
		return { floor: baseFloor, hole: true }
	}

	const lastPageRows = pageRelationRows[pageRelationRows.length - 1] ?? []
	const lastPageTruncated = lastPageRows.length > feedbackOlderMessagesPageSize
	if (!lastPageTruncated) return { floor: undefined, hole: false }

	return { floor: oldestOf(lastPageRows), hole: false }
}

export interface TimelineGating {
	mergedThread: AugmentedFeedback
	// Newest floor across incomplete relations; display cuts strictly above it.
	boundary: TimelineRow | undefined
	hasOlder: boolean
	// True while the base window needs to grow to re-overlap page 1.
	needsBaseHeal: boolean
	nextCursor: ThreadTimelineCursor | undefined
}

export function computeTimelineGating(
	thread: AugmentedFeedback,
	baseLimit: number,
	pages: TimelinePageData[],
): TimelineGating {
	const { messageLimit, activityLimit } = threadDetailRelationLimits(baseLimit)
	const pageRow = (key: keyof ThreadOlderMessagesRow) =>
		pages.map((p) => p.row?.[key] as readonly TimelineRow[] | undefined)
	const pageLiveChatRows = pages.map((p) => p.row?.liveChatSession?.messages)

	const relations = [
		{ base: thread.emails, limit: messageLimit, rows: pageRow("emails") },
		{
			base: thread.slackMessages,
			limit: messageLimit,
			rows: pageRow("slackMessages"),
		},
		{
			base: thread.chatMessages,
			limit: messageLimit,
			rows: pageRow("chatMessages"),
		},
		{
			base: thread.threadMessages,
			limit: messageLimit,
			rows: pageRow("threadMessages"),
		},
		{ base: thread.comments, limit: messageLimit, rows: pageRow("comments") },
		{
			base: thread.liveChatSession?.messages,
			limit: messageLimit,
			rows: pageLiveChatRows,
		},
		{
			base: thread.threadActivities,
			limit: activityLimit,
			rows: pageRow("threadActivities"),
		},
	]

	let boundary: TimelineRow | undefined
	let needsBaseHeal = false
	let hasOlder = false
	for (const relation of relations) {
		const gate = gateRelation(
			relation.base,
			relation.limit,
			relation.rows,
			pages,
		)
		if (gate.hole) needsBaseHeal = true
		if (!gate.floor) continue
		hasOlder = true
		if (!boundary || cmpRow(gate.floor, boundary) > 0) boundary = gate.floor
	}

	const mergedThread: AugmentedFeedback = {
		...thread,
		emails: mergeRows(thread.emails, pageRow("emails")),
		slackMessages: mergeRows(thread.slackMessages, pageRow("slackMessages")),
		chatMessages: mergeRows(thread.chatMessages, pageRow("chatMessages")),
		threadMessages: mergeRows(thread.threadMessages, pageRow("threadMessages")),
		comments: mergeRows(thread.comments, pageRow("comments")),
		threadActivities: mergeRows(
			thread.threadActivities,
			pageRow("threadActivities"),
		),
		liveChatSession: thread.liveChatSession
			? {
					...thread.liveChatSession,
					messages: mergeRows(
						thread.liveChatSession.messages,
						pageLiveChatRows,
					),
				}
			: thread.liveChatSession,
	}

	return {
		mergedThread,
		boundary,
		hasOlder,
		needsBaseHeal,
		nextCursor: boundary
			? { createdAt: boundary.createdAt, id: boundary.id }
			: undefined,
	}
}
